import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark";

/**
 * Where the chosen theme is remembered.
 *
 * The shared package deliberately knows nothing about sessions or the API: the
 * public site has no signed-in user, so it keeps the choice in the browser,
 * while the authenticated app supplies a server-backed adapter over the user's
 * stored preferences. Injecting it here is what lets one ThemeProvider serve
 * both without the design system depending on the app's API layer.
 */
export interface ThemePersistence {
  /** The stored mode, or null when nothing is stored yet. */
  mode: ThemeMode | null;
  /** False while the stored value is still unknown. */
  loaded: boolean;
  save: (mode: ThemeMode) => void;
}

const STORAGE_KEY = "mmm.themeMode";

interface ThemeContextValue {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Used until a stored preference arrives, and for signed-out screens. */
function systemMode(): ThemeMode {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStoredMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Private mode, blocked site data — fall back to the system preference.
    return null;
  }
}

/** The default when no adapter is supplied: remembered per browser only. */
function useBrowserPersistence(): ThemePersistence {
  const [mode, setMode] = useState<ThemeMode | null>(() => readStoredMode());

  const save = useCallback((next: ThemeMode) => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Nothing to do — the mode still applies for this page view.
    }
  }, []);

  return useMemo(() => ({ mode, loaded: true, save }), [mode, save]);
}

export function ThemeProvider({
  children,
  persistence,
}: {
  children: ReactNode;
  persistence?: ThemePersistence;
}) {
  const browser = useBrowserPersistence();
  const { mode: storedMode, loaded, save } = persistence ?? browser;

  // Local mirror so a toggle paints immediately rather than waiting on the
  // round trip; the stored value below wins whenever it changes.
  const [localMode, setLocalMode] = useState<ThemeMode | null>(null);
  const [lastStored, setLastStored] = useState(storedMode);

  // Adjusting state during render (React's documented "reset state when a
  // prop changes" pattern) rather than in an effect: the mirror must be
  // dropped the moment a NEW stored value arrives — from our own save, another
  // tab, or switching identity — so a stale local value can never pin the UI.
  if (loaded && storedMode !== lastStored) {
    setLastStored(storedMode);
    setLocalMode(null);
  }

  const themeMode = localMode ?? storedMode ?? systemMode();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  const applyMode = useCallback(
    (mode: ThemeMode) => {
      setLocalMode(mode);
      save(mode);
    },
    [save]
  );

  const toggleTheme = useCallback(() => {
    applyMode(themeMode === "dark" ? "light" : "dark");
  }, [applyMode, themeMode]);

  const value = useMemo(
    () => ({ themeMode, setThemeMode: applyMode, toggleTheme }),
    [themeMode, applyMode, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside ThemeProvider");
  return context;
}
